<script lang="ts">
	import { onMount } from 'svelte';
	//Design system components
	import { Modal, Text, VerificationCode, Container, Alert } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	//Helpers
	import { user_data } from 'src/stores/account/user';
	import { createEventDispatcher } from 'svelte';
	import { capFirstChar, wait } from '../helpers/utilities';
	import { BiometryService } from 'src/services/biometry/biometry-service';

	export let showModal;

	const isBiometryEnabled = BiometryService.isBiometryTurnedOnByUser();
	const currentPin = $user_data.lock_pin;
	let hasBiometryFailed = false;
	let newUserPin = '';
	let error = false;
	let verificationCodeRef: any;
	let biometricModalTranslations = {
		title: $format('biometrics_auth_title', { default: 'Biometric Unlock' }),
		subtitle: '',
		description: $format('biometrics_auth_subtitle', {
			default: 'Use your fingerprint or Face ID to authenticate.',
		}),
		cancel: $format('button_cancel', { default: 'Cancel' }),
		iosFallbackButton: $format('biometrics_auth_ios_fallback_button', {
			default: 'Use PIN',
		}),
	};

	const dispatch = createEventDispatcher();

	const checkPin = (value: string) => {
		newUserPin = value;

		if (newUserPin === currentPin) {
			dispatch('unlock');
			showModal = false;
		} else {
			error = true;
			// Note: do NOT reset `hasBiometryFailed` here. The Modal is rendered
			// by `{#if !isBiometryEnabled || hasBiometryFailed}`, so flipping it
			// back to `false` while biometry is enabled unmounts the Modal (and
			// nulls `verificationCodeRef`) before the queued `setTimeout` runs,
			// which then crashes with "Cannot read properties of null
			// (reading 'focus')". The user is on the PIN modal precisely because
			// biometry already failed for this session — keep that flag set.

			setTimeout(() => {
				newUserPin = '';
				// Defensive: the parent may unmount us between the wrong PIN
				// and this queued tick (e.g. drawer/sidepanel close).
				verificationCodeRef?.focus();
			});
		}
	};

	onMount(() => {
		if (!isBiometryEnabled) {
			showPinModal();
			return;
		}

		startBiometricAuth();
	});

	const startBiometricAuth = async () => {
		if (!BiometryService.canCallBiometricsAuth()) {
			failBiometricAuth();
			return;
		}

		const bioAuth = await BiometryService.startBiometricAuthentication(
			biometricModalTranslations.title,
			biometricModalTranslations.subtitle,
			biometricModalTranslations.description,
			biometricModalTranslations.cancel,
			biometricModalTranslations.iosFallbackButton,
		);

		if (bioAuth.error === 'Canceled' || bioAuth.error === 'userCancel') {
			hasBiometryFailed = false;
			dispatch('cancel');
			return;
		}

		if (!bioAuth.success) {
			failBiometricAuth();
			return;
		}

		dispatch('unlock');
		showModal = false;
	};

	const failBiometricAuth = () => {
		hasBiometryFailed = true;
		showPinModal();
	};

	const showPinModal = async () => {
		await wait(100);
		// Defensive: the parent may unmount us during the 100ms wait
		// (e.g. drawer close or biometry cancel just before this resolves).
		verificationCodeRef?.focus();
	};
</script>

{#if !isBiometryEnabled || hasBiometryFailed}
	<Modal
		{showModal}
		on:close={() => {
			dispatch('close');
			showModal = false;
		}}
		title={capFirstChar($format('sub_settings_title_pin_lock'))}
	>
		<svelte:fragment slot="content">
			<Container noScroll padding="0">
				{#if error}
					<Alert
						class="mb-m"
						type="danger"
						heading={$format('pin_input_error_wrong_heading', {
							default: 'Wrong pin entered.',
						})}
						isHeadingBold
						message={$format('pin_input_error_wrong_message', {
							default: 'The password you entered is incorrect. Please try again.',
						})}
					/>
				{/if}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<div class="d-flex justify-content-center align-items-center flex-direction-column pb-s">
					<Text class="mb-m text-center">{$format('pin_prompt')}</Text>
					<div style="width: 224px;">
						<form autocomplete="off">
							<VerificationCode
								id="verificationcode-i9klhd5"
								a11yProps={{
									fieldsAriaLabel: [
										$format('label_verification_code_digit', {
											default: 'Verification code digit 1',
										}),
										$format('label_verification_code_digit', {
											default: 'Verification code digit 2',
										}),
										$format('label_verification_code_digit', {
											default: 'Verification code digit 3',
										}),
										$format('label_verification_code_digit', {
											default: 'Verification code digit 4',
										}),
									],
								}}
								bind:this={verificationCodeRef}
								value={newUserPin}
								numberOfFields={4}
								isHidden
								on:value_change={(event) => {
									checkPin(event.detail.value);
								}}
								on:input={() => {
									if (error) {
										error = false;
									}
								}}
							/>
						</form>
					</div>
				</div>
			</Container>
		</svelte:fragment>
	</Modal>
{/if}
